/**
 * Loads and keeps fresh the Bot Ops snapshot.
 *
 *  - Polls every 60 s while the tab is visible, every 5 min while hidden, and
 *    refetches straight away when the tab becomes visible again.
 *  - Sends the last ETag, so an unchanged snapshot is a 304 with no body.
 *  - NEVER blanks the screen on a failed poll: the last good snapshot stays and
 *    `error` is set alongside it. (The older admin hooks reset to zeros on a
 *    fetch failure, which showed $0 as if it were real.) Before any data has
 *    loaded, `error` is the only thing there is, and the page shows an error
 *    card, not zeros.
 *  - New data is held, not applied, while `hold` is true (someone is typing in a
 *    composer), so a refresh cannot eat their text. `applyPending()` applies it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { BotOpsHttpError } from '../api/botOpsClient';
import { getSnapshot } from '../api/botOpsService';
import { Snapshot, SnapshotMeta } from '../lib/types';

export const POLL_VISIBLE_MS = 60 * 1000;
export const POLL_HIDDEN_MS = 5 * 60 * 1000;

export interface SnapshotState {
  data: Snapshot | null;
  meta: SnapshotMeta | null;
  error: BotOpsHttpError | null;
  /** True only until the first response (data or error) arrives. */
  loading: boolean;
  /** True while a request is in flight. */
  polling: boolean;
  lastSyncedAt: number | null;
  hasPending: boolean;
}

interface Pending { data: Snapshot; meta: SnapshotMeta | null; etag: string | null }

const INITIAL: SnapshotState = { data: null, meta: null, error: null, loading: true, polling: false, lastSyncedAt: null, hasPending: false };

export function useSnapshot({ hold = false }: { hold?: boolean } = {}) {
  const [state, setState] = useState<SnapshotState>(INITIAL);
  const etagRef = useRef<string | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  const holdRef = useRef(hold);
  const hasDataRef = useRef(false);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  holdRef.current = hold;

  const fetchOnce = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (mounted.current) setState((s) => ({ ...s, polling: true }));
    try {
      const r = await getSnapshot(etagRef.current);
      if (!mounted.current) return;
      if (r.notModified || !r.data) {
        setState((s) => ({ ...s, polling: false, loading: false, error: null, lastSyncedAt: Date.now() }));
        return;
      }
      const meta = (r.meta as unknown as SnapshotMeta) || null;
      if (holdRef.current && hasDataRef.current) {
        pendingRef.current = { data: r.data, meta, etag: r.etag };
        setState((s) => ({ ...s, polling: false, error: null, lastSyncedAt: Date.now(), hasPending: true }));
        return;
      }
      etagRef.current = r.etag;
      hasDataRef.current = true;
      pendingRef.current = null;
      setState({ data: r.data, meta, error: null, loading: false, polling: false, lastSyncedAt: Date.now(), hasPending: false });
    } catch (e) {
      if (!mounted.current) return;
      const err = e instanceof BotOpsHttpError ? e : new BotOpsHttpError((e as Error).message || 'Unknown error', 0, 'UNKNOWN');
      setState((s) => ({ ...s, polling: false, loading: false, error: err }));
    } finally {
      inFlight.current = false;
    }
  }, []);

  const applyPending = useCallback(() => {
    const p = pendingRef.current;
    if (!p) return;
    etagRef.current = p.etag;
    pendingRef.current = null;
    hasDataRef.current = true;
    setState((s) => ({ ...s, data: p.data, meta: p.meta, hasPending: false }));
  }, []);

  useEffect(() => {
    mounted.current = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const loop = async () => {
      await fetchOnce();
      if (cancelled) return;
      timer = setTimeout(loop, document.visibilityState === 'visible' ? POLL_VISIBLE_MS : POLL_HIDDEN_MS);
    };
    loop();

    const onVisible = () => {
      if (document.visibilityState !== 'visible' || cancelled) return;
      if (timer) clearTimeout(timer);
      loop();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      mounted.current = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchOnce]);

  return { ...state, refresh: fetchOnce, applyPending };
}
